import { parseISO, isValid, format } from 'date-fns'
import { enGB } from 'date-fns/locale'
import moment from 'moment'
import type React from 'react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import EditButton from '@/components/EditButton'
import TooltipIcon from '@/components/IconGroup/TooltipIcon'
import ReferenceOutcomeStatusPill from '@/components/ReferenceOutcomeStatusPill'
import SmallPill from '@/components/SmallPill'
import SupportingDocuments from '@/components/SupportingDocuments'
import type { Application, EmployerReference } from '@/types/Activity'
import { parseDate, toDateLabel } from '@/utils/date'
import { fadeIn } from '@/utils/styles'

import EntityPanelHeader from './EntityPanelHeader'
import EditRefereePanel from './Referee'
import { ContractExtensionMapping, employerRelationshipMapping } from './utils'

const Container = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 1px 9px 0 rgba(128, 128, 128, 0.26);
  margin: 10px;
  background: white;
  animation: ${fadeIn} 0.22s ease-in-out 0s forwards 1;

  h4 {
    color: #34bebd;
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
  }
`

const Body = styled.div``

const ItemHeader = styled.h2`
  font-family: Montserrat;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  -webkit-letter-spacing: 0.21px;
  -moz-letter-spacing: 0.21px;
  -ms-letter-spacing: 0.21px;
  letter-spacing: 0.21px;
  margin-bottom: 0;

  #document-proof {
    font-weight: normal;
  }
`

const SmallIcon = styled.img`
  width: 12px;
  height: 12px;
  margin-right: 5px;
`

const EmploymentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  grid-gap: 10px;
`

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
  align-items: center;
`

interface Props {
  application: Application
}

const EmployerReferencePanel: React.FC<Props> = ({
  application: {
    customerId,
    subjectId,
    context,
    attachedSubjects,
    reference,
    milestones,
  },
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false)

  const history = useHistory()

  const employerReference = reference as EmployerReference

  function handleEmployeeToggled(employeeId: string) {
    history.push(`/applicant/${employeeId}`)
  }

  if (!employerReference || !attachedSubjects) {
    return (
      <Container>
        <EntityPanelHeader name="Employer Reference" icon="employment" />
        <p>Nothing here yet!</p>
      </Container>
    )
  }

  const completedMilestone = milestones.find(
    milestone => milestone.event === 'subject.complete',
  )

  const isEditDisabled = attachedSubjects.length > 1 || completedMilestone !== undefined

  return (
    <>
      {isPanelOpen && (
        <EditRefereePanel
          subjectData={{
            customerId,
            subjectId,
            name: context?.name ?? '',
            email: context?.email ?? '',
            phone: context?.mobile ?? '',
          }}
          setOpen={() => setIsPanelOpen(false)}
          refereeType="employer"
        />
      )}
      <Container>
        <div className="side-by-side-flex">
          <EntityPanelHeader name="Employer Reference" icon="employment" />{' '}
          {isEditDisabled ? (
            <TooltipIcon
              tooltipMessage={`${
                attachedSubjects.length > 1
                  ? 'Cannot edit referee details as the referee has multiple attached applicants'
                  : 'Cannot edit referee details as the referee has completed the journey'
              }`}
              icon={
                <EditButton
                  isDisabled={isEditDisabled}
                  onClick={() => setIsPanelOpen(true)}
                />
              }
            />
          ) : (
            <EditButton onClick={() => setIsPanelOpen(true)} />
          )}
        </div>
        <p>
          <strong>{context?.firstName}</strong> has been added as an employer reference
          for <strong>{attachedSubjects && attachedSubjects[0].name}</strong>
        </p>
        {attachedSubjects && (
          <div className="side-by-side-flex-end">
            <SmallPill
              onClick={() => handleEmployeeToggled(attachedSubjects[0].subjectId)}
            >
              View Linked Employee
            </SmallPill>
          </div>
        )}
        <ItemGrid>
          <div>
            <ItemHeader>Name</ItemHeader>
            <p>
              {context?.firstName} {context?.lastName}
            </p>
          </div>
          <div>
            <ItemHeader>Email</ItemHeader>
            <p>{context?.email}</p>
          </div>
          <div>
            <ItemHeader>Phone</ItemHeader>
            <p>{context?.mobile}</p>
          </div>
          {context?.jobRole && (
            <div>
              <ItemHeader>Job Title</ItemHeader>
              <p>{context?.jobRole}</p>
            </div>
          )}
        </ItemGrid>
        <hr />
        <div className="side-by-side-flex">
          <h2>Outcome</h2>
          <ReferenceOutcomeStatusPill
            referenceOutcomeStatus={employerReference.outcome.status}
          />
        </div>
        {completedMilestone && (
          <div>
            <ItemHeader>References submitted</ItemHeader>
            <p>
              {toDateLabel({
                date: parseDate(completedMilestone.createdAt),
                dateFormat: 'dd/MM/yyyy HH:mm:ss',
              })}
            </p>
          </div>
        )}
        <div>
          {employerReference.additionalContext && (
            <div>
              <ItemHeader>Additional Context</ItemHeader>
              {!employerReference?.outcome && employerReference.additionalContext && (
                <i>
                  The employer reference has elected to add the following additional
                  information.
                </i>
              )}
              <p>{employerReference.additionalContext}</p>
            </div>
          )}
        </div>
        <div>
          <ItemHeader>Decision Comments</ItemHeader>
          {employerReference?.outcome?.comments &&
            employerReference.outcome.comments.length > 0 && (
              <ul>
                {employerReference.outcome.comments.map((comment, index) => (
                  <li key={index}>{comment}</li>
                ))}
              </ul>
            )}
          {(!employerReference?.outcome?.comments ||
            employerReference?.outcome?.comments.length === 0) && (
            <p>No comments provided.</p>
          )}
          {!employerReference.outcome && <p>Nothing here yet!</p>}
          {!employerReference?.outcome?.comments ||
            (employerReference?.outcome?.comments.length === 0 &&
              'Comments on this decision is not currently available')}
        </div>
        {employerReference.income && employerReference.income.currency && (
          <Body>
            <div>
              <ItemHeader>Value of the income</ItemHeader>
              <p>
                {employerReference.income.currency.symbol}
                {employerReference.income.value}{' '}
                <span>
                  paid <strong>{employerReference.income.frequency}</strong>{' '}
                  {employerReference.income.averageHoursAWeek && (
                    <span>
                      ({employerReference.income.averageHoursAWeek} per week on average)
                    </span>
                  )}
                </span>
              </p>
            </div>
            {employerReference.guaranteedBonus &&
              employerReference.guaranteedBonus.currency && (
                <div>
                  <ItemHeader>Value of the bonus</ItemHeader>
                  <p>
                    {employerReference.guaranteedBonus.currency.symbol}
                    {employerReference.guaranteedBonus.value}{' '}
                    <span>
                      paid{' '}
                      <strong>{employerReference.guaranteedBonus.frequency}</strong>{' '}
                    </span>
                  </p>
                </div>
              )}
            <>
              <EmploymentDetails>
                <div>
                  <ItemHeader>Position held</ItemHeader>
                  <p>{employerReference.jobTitle}</p>
                  {employerReference.refereeRelationship && (
                    <>
                      <ItemHeader>Relationship</ItemHeader>
                      <p>
                        {employerRelationshipMapping[
                          employerReference.refereeRelationship
                        ] ?? employerReference.refereeRelationship}
                      </p>
                    </>
                  )}
                  <ItemHeader>Contract Type</ItemHeader>
                  <p>{employerReference.contractType}</p>
                </div>
              </EmploymentDetails>
              <EmploymentDetails>
                <div>
                  <ItemHeader>Employment Basis</ItemHeader>
                  <p>{employerReference.contractFrequency}</p>
                </div>
                <div>
                  <ItemHeader>Employment Started</ItemHeader>
                  <div className="side-by-side-flex-start">
                    {' '}
                    <SmallIcon src="/assets/svg/calendar.svg" alt="" />{' '}
                    <p>
                      {' '}
                      {moment(employerReference.startDate).locale('en-gb').format('LL')}
                    </p>
                  </div>
                </div>
                {employerReference.endDate &&
                  isValid(parseISO(employerReference.endDate)) &&
                  parseISO(employerReference.endDate).getTime() > 0 && (
                    <>
                      <div>
                        <ItemHeader>Employment is due to end</ItemHeader>
                        <div className="side-by-side-flex-start">
                          {' '}
                          <SmallIcon src="/assets/svg/calendar.svg" alt="" />{' '}
                          <p>
                            {' '}
                            {format(parseISO(employerReference.endDate), 'PPPP', {
                              locale: enGB,
                            })}
                          </p>
                        </div>
                      </div>
                      {employerReference.contractExtension && (
                        <div>
                          <ItemHeader>Contract Extension</ItemHeader>
                          <p>
                            {
                              ContractExtensionMapping[
                                employerReference.contractExtension
                              ]
                            }
                          </p>
                        </div>
                      )}
                    </>
                  )}
              </EmploymentDetails>
            </>
          </Body>
        )}
        {employerReference.supportingDocuments && (
          <Body>
            <ItemHeader>Supporting Documents</ItemHeader>
            <p>
              The employer referee has elected to upload documents to prove the reference.
            </p>
            <SupportingDocuments
              supportingDocuments={employerReference.supportingDocuments}
              customerId={customerId}
              subjectId={subjectId}
            />
          </Body>
        )}
      </Container>
    </>
  )
}

export default EmployerReferencePanel
